// CustomersTab.jsx - Customers section in Accounting module
// Tracks: 1) Outside Service (customer repair work) 2) Vehicle Sales (retail/auction/dealer)
import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const CustomersTab = () => {
  const [activeTab, setActiveTab] = useState('sales'); // 'service' or 'sales'
  const [serviceData, setServiceData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [service, sales] = await Promise.all([
        api.request('/accounting/customers/service'),
        api.request('/accounting/customers/sales')
      ]);
      setServiceData(service);
      setSalesData(sales);
    } catch (err) {
      console.error('Failed to load customer data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const filteredService = serviceData.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      item.customerName?.toLowerCase().includes(s) ||
      item.customerPhone?.includes(s) ||
      item.roNumber?.toLowerCase().includes(s) ||
      `${item.customerVehicleYear} ${item.customerVehicleMake} ${item.customerVehicleModel}`.toLowerCase().includes(s)
    );
  });

  const filteredSales = salesData.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      item.saleBuyerName?.toLowerCase().includes(s) ||
      item.saleBuyerPhone?.includes(s) ||
      item.stockNumber?.toLowerCase().includes(s) ||
      `${item.year} ${item.make} ${item.model}`.toLowerCase().includes(s)
    );
  });

  // Payment Modal Component
  const PaymentModal = ({ item, type, onClose, onSave }) => {
    const [form, setForm] = useState({
      paidAmount: item.customerPaidAmount || item.salePaidAmount || 0,
      paymentMethod: item.customerPaymentMethod || item.salePaymentMethod || '',
      paymentDate: item.customerPaymentDate || item.salePaymentDate || new Date().toISOString().split('T')[0],
      invoiceTotal: item.customerInvoiceTotal || item.salePrice || 0,
      buyerName: item.saleBuyerName || '',
      buyerType: item.saleBuyerType || 'retail',
      buyerPhone: item.saleBuyerPhone || '',
      buyerEmail: item.saleBuyerEmail || '',
      titleSent: item.titleSent || false,
      titleSentDate: item.titleSentDate || ''
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      setSaving(true);
      try {
        if (type === 'service') {
          await api.request(`/accounting/customers/service/${item.id}/payment`, {
            method: 'PUT',
            body: JSON.stringify({
              paidAmount: parseFloat(form.paidAmount) || 0,
              paymentMethod: form.paymentMethod,
              paymentDate: form.paymentDate,
              invoiceTotal: parseFloat(form.invoiceTotal) || 0
            })
          });
        } else {
          await api.request(`/accounting/customers/sales/${item.id}/payment`, {
            method: 'PUT',
            body: JSON.stringify({
              buyerName: form.buyerName,
              buyerType: form.buyerType,
              buyerPhone: form.buyerPhone,
              buyerEmail: form.buyerEmail,
              paidAmount: parseFloat(form.paidAmount) || 0,
              paymentMethod: form.paymentMethod,
              paymentDate: form.paymentDate,
              titleSent: form.titleSent,
              titleSentDate: form.titleSentDate || null
            })
          });
        }
        onSave();
        onClose();
      } catch (err) {
        alert('Failed to save: ' + err.message);
      } finally {
        setSaving(false);
      }
    };

    const total = parseFloat(form.invoiceTotal) || 0;
    const paid = parseFloat(form.paidAmount) || 0;
    const balance = total - paid;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-neutral-200">
            <h2 className="text-xl font-bold">
              {type === 'service' ? 'Service Payment' : 'Sale Payment'}
            </h2>
            <p className="text-neutral-500 text-sm mt-1">
              {type === 'service' 
                ? `${item.roNumber} - ${item.customerName || 'Customer'}`
                : `${item.stockNumber} - ${item.year} ${item.make} ${item.model}`
              }
            </p>
          </div>

          <div className="p-6 space-y-4">
            {type === 'sales' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Buyer Name</label>
                    <input
                      type="text"
                      value={form.buyerName}
                      onChange={e => setForm({...form, buyerName: e.target.value})}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                      placeholder="Customer/Dealer name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Buyer Type</label>
                    <select
                      value={form.buyerType}
                      onChange={e => setForm({...form, buyerType: e.target.value})}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                    >
                      <option value="retail">Retail Customer</option>
                      <option value="dealer">Dealer</option>
                      <option value="auction">Auction</option>
                      <option value="wholesale">Wholesale</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={form.buyerPhone}
                      onChange={e => setForm({...form, buyerPhone: e.target.value})}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={form.buyerEmail}
                      onChange={e => setForm({...form, buyerEmail: e.target.value})}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {type === 'service' ? 'Invoice Total' : 'Sale Price'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                  <input
                    type="number"
                    value={form.invoiceTotal}
                    onChange={e => setForm({...form, invoiceTotal: e.target.value})}
                    className="w-full pl-7 pr-3 py-2 border border-neutral-300 rounded-lg"
                    readOnly={type === 'sales'}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Amount Paid</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                  <input
                    type="number"
                    value={form.paidAmount}
                    onChange={e => setForm({...form, paidAmount: e.target.value})}
                    className="w-full pl-7 pr-3 py-2 border border-neutral-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Method</label>
                <select
                  value={form.paymentMethod}
                  onChange={e => setForm({...form, paymentMethod: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                >
                  <option value="">-- Select --</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="ACH">ACH</option>
                  <option value="Wire">Wire</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Financing">Financing</option>
                  <option value="Floor Plan">Floor Plan</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={form.paymentDate}
                  onChange={e => setForm({...form, paymentDate: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                />
              </div>
            </div>

            {type === 'sales' && (
              <div className="flex items-center gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.titleSent}
                    onChange={e => setForm({...form, titleSent: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Title Sent</span>
                </label>
                {form.titleSent && (
                  <input
                    type="date"
                    value={form.titleSentDate}
                    onChange={e => setForm({...form, titleSentDate: e.target.value})}
                    className="px-3 py-1 border border-neutral-300 rounded-lg text-sm"
                    placeholder="Date sent"
                  />
                )}
              </div>
            )}

            {/* Balance Summary */}
            <div className="bg-neutral-100 rounded-lg p-4 mt-4">
              <div className="flex justify-between text-sm">
                <span>Total:</span>
                <span className="font-medium">{formatCurrency(total)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Paid:</span>
                <span className="font-medium text-green-600">{formatCurrency(paid)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-neutral-300 pt-2 mt-2">
                <span>Balance Due:</span>
                <span className={balance > 0 ? 'text-red-600' : 'text-green-600'}>
                  {formatCurrency(balance)}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-neutral-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Payment'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-500">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Customers</h2>
        <input
          type="text"
          placeholder="Search by name, phone, vehicle..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-4 py-2 border border-neutral-300 rounded-lg w-72"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200">
        <button
          onClick={() => setActiveTab('sales')}
          className={`px-4 py-2 font-medium border-b-2 transition ${
            activeTab === 'sales' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          Vehicle Sales ({salesData.length})
        </button>
        <button
          onClick={() => setActiveTab('service')}
          className={`px-4 py-2 font-medium border-b-2 transition ${
            activeTab === 'service' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          Outside Service ({serviceData.length})
        </button>
      </div>

      {/* Vehicle Sales Table */}
      {activeTab === 'sales' && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Stock #</th>
                <th className="px-4 py-3 text-left font-medium">Vehicle</th>
                <th className="px-4 py-3 text-left font-medium">Buyer</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium">Sale Date</th>
                <th className="px-4 py-3 text-right font-medium">Sale Price</th>
                <th className="px-4 py-3 text-right font-medium">Paid</th>
                <th className="px-4 py-3 text-center font-medium">Title</th>
                <th className="px-4 py-3 text-center font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filteredSales.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-neutral-500">
                    No vehicle sales found
                  </td>
                </tr>
              ) : (
                filteredSales.map(item => {
                  const balance = (parseFloat(item.salePrice) || 0) - (parseFloat(item.salePaidAmount) || 0);
                  const isPaid = balance <= 0;
                  return (
                    <tr key={item.id} className="hover:bg-neutral-50">
                      <td className="px-4 py-3 font-mono">{item.stockNumber}</td>
                      <td className="px-4 py-3">{item.year} {item.make} {item.model}</td>
                      <td className="px-4 py-3">{item.saleBuyerName || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.saleBuyerType === 'retail' ? 'bg-blue-100 text-blue-700' :
                          item.saleBuyerType === 'dealer' ? 'bg-purple-100 text-purple-700' :
                          item.saleBuyerType === 'auction' ? 'bg-amber-100 text-amber-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {item.saleBuyerType || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{formatDate(item.soldAt || item.saleDate)}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.salePrice)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={isPaid ? 'text-green-600' : 'text-amber-600'}>
                          {formatCurrency(item.salePaidAmount || 0)}
                        </span>
                        {!isPaid && balance > 0 && (
                          <div className="text-xs text-red-500">
                            Due: {formatCurrency(balance)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.titleSent ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setPaymentModal({ item, type: 'sales' })}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          {isPaid ? 'View' : 'Payment'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Outside Service Table */}
      {activeTab === 'service' && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium">RO #</th>
                <th className="px-4 py-3 text-left font-medium">Customer</th>
                <th className="px-4 py-3 text-left font-medium">Phone</th>
                <th className="px-4 py-3 text-left font-medium">Vehicle</th>
                <th className="px-4 py-3 text-left font-medium">Date</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Invoice</th>
                <th className="px-4 py-3 text-right font-medium">Paid</th>
                <th className="px-4 py-3 text-center font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filteredService.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-neutral-500">
                    No outside service records found
                  </td>
                </tr>
              ) : (
                filteredService.map(item => {
                  const total = parseFloat(item.customerInvoiceTotal) || parseFloat(item.partsTotal) + parseFloat(item.laborTotal) || 0;
                  const paid = parseFloat(item.customerPaidAmount) || 0;
                  const balance = total - paid;
                  const isPaid = balance <= 0 && total > 0;
                  return (
                    <tr key={item.id} className="hover:bg-neutral-50">
                      <td className="px-4 py-3 font-mono">{item.roNumber}</td>
                      <td className="px-4 py-3 font-medium">{item.customerName || '-'}</td>
                      <td className="px-4 py-3">{item.customerPhone || '-'}</td>
                      <td className="px-4 py-3">
                        {item.customerVehicleYear} {item.customerVehicleMake} {item.customerVehicleModel}
                      </td>
                      <td className="px-4 py-3">{formatDate(item.createdAt)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.status === 'complete' ? 'bg-green-100 text-green-700' :
                          item.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(total)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={isPaid ? 'text-green-600' : 'text-amber-600'}>
                          {formatCurrency(paid)}
                        </span>
                        {!isPaid && balance > 0 && (
                          <div className="text-xs text-red-500">
                            Due: {formatCurrency(balance)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setPaymentModal({ item: {...item, customerInvoiceTotal: total}, type: 'service' })}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          {isPaid ? 'View' : 'Payment'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <PaymentModal
          item={paymentModal.item}
          type={paymentModal.type}
          onClose={() => setPaymentModal(null)}
          onSave={loadData}
        />
      )}
    </div>
  );
};

export default CustomersTab;
